import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import "./DateTimePicker.scss";

// ─────────────────────────────────────────────────────────────────────────────
// Button-triggered date and time pickers.
//
// Neither field is typable: a native <input type="date"> lets a coach hand-type
// a malformed or out-of-range value and renders differently in every browser.
// Here the trigger is a button showing the formatted value, and the value can
// only be set by choosing from the popover.
//
// The popovers portal to document.body and position themselves with `fixed`
// against the trigger's bounding box. That's what lets the host dialog keep a
// capped height and scroll internally: an absolutely-positioned popover inside
// a scroll container gets clipped by it, and padding the container to make room
// shoves the layout around every time a picker opens. Portalling sidesteps
// both — the popup's own height never changes.
// ─────────────────────────────────────────────────────────────────────────────

const pad = (value) => String(value).padStart(2, "0");

export const toISODate = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const parseISODate = (iso) => {
  if (!iso) return null;
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day);
};

// Midnight today — the earliest a session can be booked.
//
// ⚠️ THE FLOOR IS TODAY, NOT TOMORROW (changed 2026-09-09, on instruction).
// This comment used to justify the old behaviour with "a review sent now can't
// reasonably be discussed live the same day" — which turned out to be exactly
// backwards for how coaches work.
// It was `+ 1`, which greyed today out of the calendar entirely — so a coach
// wrapping up a review at 9am could not book the session they were about to
// have that afternoon, and had to send the review unscheduled and follow up
// separately.
//
// ⚠️ SHARED BY BOTH MODALS. Strategy Canvas's and Execution Blueprint's
// Complete Review dialogs both call this, so today became bookable in both at
// once. That is the intent.
//
// Known consequence, deliberately left alone: with today selectable, a coach
// can pick a time earlier than the current one and book a session in the past.
// The picker has no concept of "now" — only of dates — so guarding that means
// teaching the TIME field about the selected date, which is a larger change
// than this one and is not obviously wanted (a coach logging a session that
// just happened is a plausible use).
export const getEarliestBookableDate = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

export const formatDateLabel = (iso) => {
  const date = parseISODate(iso);
  if (!date) return "";
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const formatTimeLabel = (value) => {
  if (!value) return "";
  const [hour, minute] = value.split(":").map(Number);
  return new Date(2000, 0, 1, hour, minute).toLocaleTimeString("en-GB", {
    hour: "numeric",
    minute: "2-digit",
  });
};

// Popover widths are fixed so the anchor maths can run before the popover has
// rendered — height is measured afterwards, since it varies by month length.
const POP_WIDTH = { left: 286, right: 218 };
const GAP = 6;
const VIEWPORT_MARGIN = 8;

const PickerField = ({
  label,
  icon,
  placeholder,
  displayValue,
  error,
  align = "left",
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const popRef = useRef(null);

  const width = POP_WIDTH[align];

  // Anchor to the trigger's viewport box. Flips above when there isn't room
  // below, and clamps horizontally so it can never sit off-screen.
  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const popHeight = popRef.current?.offsetHeight || 0;

    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp =
      popHeight > 0 && spaceBelow < popHeight + GAP + VIEWPORT_MARGIN;

    const left = align === "right" ? rect.right - width : rect.left;
    const clampedLeft = Math.max(
      VIEWPORT_MARGIN,
      Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN),
    );

    setPosition({
      top: flipUp ? rect.top - popHeight - GAP : rect.bottom + GAP,
      left: clampedLeft,
    });
  }, [align, width]);

  // Measure once the popover is in the DOM so the flip decision uses its real
  // height rather than a guess.
  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  // Keep it pinned while the dialog behind it scrolls. Capture phase, because
  // the scroll happens on an inner container, not on window.
  useEffect(() => {
    if (!open) return undefined;

    const handleReposition = () => reposition();
    const handleKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const handleClick = (event) => {
      const insideTrigger = triggerRef.current?.contains(event.target);
      const insidePop = popRef.current?.contains(event.target);
      if (!insideTrigger && !insidePop) setOpen(false);
    };

    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, reposition]);

  const handleToggle = () => {
    setPosition(null);
    setOpen((prev) => !prev);
  };

  return (
    <div className="dtp-field">
      <span className="dtp-label">{label}</span>

      <button
        type="button"
        ref={triggerRef}
        className={`dtp-trigger${open ? " dtp-trigger--open" : ""}${
          error ? " dtp-trigger--error" : ""
        }`}
        onClick={handleToggle}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <i className={`dtp-trigger-icon ${icon}`} aria-hidden="true" />
        <span
          className={`dtp-trigger-value${displayValue ? "" : " dtp-trigger-value--empty"}`}
        >
          {displayValue || placeholder}
        </span>
        <i
          className="dtp-trigger-caret fa-solid fa-chevron-down"
          aria-hidden="true"
        />
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={popRef}
            className="dtp-pop"
            role="dialog"
            style={{
              width,
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
              // Hidden for the first paint only, while the height is measured
              // and the flip decided — avoids a visible jump.
              visibility: position ? "visible" : "hidden",
            }}
          >
            {children({ close: () => setOpen(false) })}
          </div>,
          document.body,
        )}

      {error && <span className="dtp-error">{error}</span>}
    </div>
  );
};

// ── Date ─────────────────────────────────────────────────────────────────────
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
// Monday-first, matching how the rest of the product shows weeks.
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

export const DatePickerField = ({
  label = "Date",
  value,
  onChange,
  minDate,
  error,
}) => {
  const floor = minDate || getEarliestBookableDate();
  const [viewMonth, setViewMonth] = useState(() => {
    const anchor = parseISODate(value) || floor;
    return new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  });

  // Follow the value when it's set from outside (a preset button, say), so the
  // calendar opens on the month that's actually selected.
  useEffect(() => {
    const selected = parseISODate(value);
    if (selected)
      setViewMonth(new Date(selected.getFullYear(), selected.getMonth(), 1));
  }, [value]);

  const days = useMemo(() => {
    const firstOfMonth = new Date(
      viewMonth.getFullYear(),
      viewMonth.getMonth(),
      1,
    );
    const dayCount = new Date(
      viewMonth.getFullYear(),
      viewMonth.getMonth() + 1,
      0,
    ).getDate();
    const lead = (firstOfMonth.getDay() + 6) % 7;

    const cells = Array.from({ length: lead }, () => null);
    for (let day = 1; day <= dayCount; day += 1) {
      cells.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day));
    }
    return cells;
  }, [viewMonth]);

  // Nothing before the first bookable month is reachable, so the back arrow
  // switches off rather than paging into a month of disabled days.
  const atFloorMonth =
    viewMonth.getFullYear() === floor.getFullYear() &&
    viewMonth.getMonth() === floor.getMonth();

  const shiftMonth = (delta) =>
    setViewMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1),
    );

  return (
    <PickerField
      label={label}
      icon="fa-regular fa-calendar"
      placeholder="Select a date"
      displayValue={formatDateLabel(value)}
      error={error}
      align="left"
    >
      {({ close }) => (
        <div className="dtp-cal">
          <div className="dtp-cal-head">
            <button
              type="button"
              className="dtp-cal-nav"
              onClick={() => shiftMonth(-1)}
              disabled={atFloorMonth}
              aria-label="Previous month"
            >
              <i className="fa-solid fa-chevron-left" aria-hidden="true" />
            </button>
            <span className="dtp-cal-month">
              {MONTH_NAMES[viewMonth.getMonth()]} {viewMonth.getFullYear()}
            </span>
            <button
              type="button"
              className="dtp-cal-nav"
              onClick={() => shiftMonth(1)}
              aria-label="Next month"
            >
              <i className="fa-solid fa-chevron-right" aria-hidden="true" />
            </button>
          </div>

          <div className="dtp-cal-grid">
            {WEEKDAYS.map((weekday, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <span className="dtp-cal-dow" key={`${weekday}-${index}`}>
                {weekday}
              </span>
            ))}

            {days.map((day, index) =>
              day ? (
                <button
                  type="button"
                  key={toISODate(day)}
                  className={`dtp-cal-day${
                    toISODate(day) === value ? " dtp-cal-day--selected" : ""
                  }`}
                  disabled={day < floor}
                  onClick={() => {
                    onChange(toISODate(day));
                    close();
                  }}
                >
                  {day.getDate()}
                </button>
              ) : (
                // eslint-disable-next-line react/no-array-index-key
                <span
                  className="dtp-cal-day dtp-cal-day--blank"
                  key={`blank-${index}`}
                />
              ),
            )}
          </div>

          <p className="dtp-cal-hint">
            <i className="fa-regular fa-circle-question" aria-hidden="true" />
            Pick any date from today.
          </p>
        </div>
      )}
    </PickerField>
  );
};

// ── Time ─────────────────────────────────────────────────────────────────────
const START_HOUR = 8;
const END_HOUR = 19;

export const TimePickerField = ({ label = "Time", value, onChange, error }) => {
  const slots = useMemo(() => {
    const out = [];
    for (let hour = START_HOUR; hour <= END_HOUR; hour += 1) {
      [0, 30].forEach((minute) => out.push(`${pad(hour)}:${pad(minute)}`));
    }
    return out;
  }, []);

  return (
    <PickerField
      label={label}
      icon="fa-regular fa-clock"
      placeholder="Select a time"
      displayValue={formatTimeLabel(value)}
      error={error}
      align="right"
    >
      {({ close }) => (
        <div className="dtp-times">
          {slots.map((slot) => (
            <button
              type="button"
              key={slot}
              className={`dtp-time${slot === value ? " dtp-time--selected" : ""}`}
              onClick={() => {
                onChange(slot);
                close();
              }}
            >
              {formatTimeLabel(slot)}
            </button>
          ))}
        </div>
      )}
    </PickerField>
  );
};
